import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ApiService } from '../../services/api.service';
import { VersionLog, S3Status, S3ExportResult } from '../../models/aws-export.model';
import { WorkstationRoleType } from '../../models/client-management.model';
import { environment } from '../../../environments/environment';

@Component({
  selector: 'app-version-logs',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './version-logs.component.html',
  styleUrl: './version-logs.component.scss'
})
export class VersionLogsComponent implements OnInit {
  logs: VersionLog[] = [];
  filteredLogs: VersionLog[] = [];
  loading = false;
  error = '';
  s3Status: S3Status | null = null;

  isExternal = environment.externalAdminAuth;

  // Filters
  selectedDatabase = '';
  selectedRoleTypeId: number | null = null;
  showPendingOnly = false;

  trackedDatabases: string[] = [];
  roleTypes: WorkstationRoleType[] = [];

  // Retry state
  retryingIds = new Set<number>();
  retryResults = new Map<number, S3ExportResult>();

  constructor(private apiService: ApiService) {}

  ngOnInit(): void {
    this.apiService.getS3Status().subscribe({
      next: s => (this.s3Status = s),
      error: err => console.error('Failed to load S3 status:', err)
    });

    if (!this.isExternal) {
      this.apiService.getTrackedDatabasesForVersionLogs().subscribe({
        next: dbs => (this.trackedDatabases = dbs),
        error: err => console.error('Failed to load tracked databases:', err)
      });
    }

    this.apiService.getRoleTypes().subscribe({
      next: roles => (this.roleTypes = roles),
      error: err => console.error('Failed to load role types:', err)
    });
    this.loadLogs();
  }

  loadLogs(): void {
    this.loading = true;
    this.error = '';

    // On external: load all (backend already filters by mapped DBs), filter client-side
    // On internal: pass filters to backend as usual
    const dbFilter = this.isExternal ? undefined : (this.selectedDatabase || undefined);

    this.apiService.getVersionLogs(dbFilter, this.selectedRoleTypeId ?? undefined, this.showPendingOnly).subscribe({
      next: logs => {
        this.logs = logs;

        if (this.isExternal) {
          // Derive dropdown options from loaded logs
          const targets = [...new Set(logs.map(l => l.targetDatabase).filter((d): d is string => !!d))].sort();
          this.trackedDatabases = targets;
          this.applyExternalFilter();
        } else {
          this.filteredLogs = logs;
        }

        this.loading = false;
      },
      error: err => {
        this.error = 'Failed to load version logs: ' + (err.error?.message || err.message);
        this.loading = false;
      }
    });
  }

  applyExternalFilter(): void {
    this.filteredLogs = this.logs.filter(l => {
      const dbMatch = !this.selectedDatabase || l.targetDatabase === this.selectedDatabase;
      const pendingMatch = !this.showPendingOnly || !l.isUploaded;
      return dbMatch && pendingMatch;
    });
  }

  onFilterChange(): void {
    if (this.isExternal) {
      this.applyExternalFilter();
    } else {
      this.loadLogs();
    }
  }

  retryExport(log: VersionLog): void {
    if (this.retryingIds.has(log.exportLogID)) return;
    this.retryingIds.add(log.exportLogID);
    this.retryResults.delete(log.exportLogID);

    this.apiService.retryAwsExport(log.exportLogID).subscribe({
      next: result => {
        this.retryResults.set(log.exportLogID, result);
        this.retryingIds.delete(log.exportLogID);
        if (result.success) setTimeout(() => this.loadLogs(), 1000);
      },
      error: err => {
        this.retryResults.set(log.exportLogID, {
          success: false,
          errorMessage: err.error?.message || err.message
        });
        this.retryingIds.delete(log.exportLogID);
      }
    });
  }

  isRetrying(id: number): boolean { return this.retryingIds.has(id); }
  getRetryResult(id: number): S3ExportResult | undefined { return this.retryResults.get(id); }

  formatDate(date: Date | string | undefined): string {
    if (!date) return '-';
    return new Date(date).toLocaleDateString('en-GB', {
      day: '2-digit', month: 'short', year: 'numeric'
    });
  }

  truncatePath(path: string | undefined, len = 50): string {
    if (!path) return '';
    if (path.length <= len) return path;
    const parts = path.split('/');
    const file = parts[parts.length - 1];
    return '…/' + file;
  }

  get uploadedCount(): number { return this.filteredLogs.filter(l => l.isUploaded).length; }
  get pendingCount(): number { return this.filteredLogs.filter(l => !l.isUploaded).length; }
  get artifactPendingCount(): number {
    return this.filteredLogs.reduce((sum, l) =>
      sum + (l.roleArtifacts?.filter(a => !a.s3Path).length ?? 0), 0);
  }
}
